/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { DefaultButton, PrimaryButton } from '@fluentui/react/lib/Button';
import { Dialog, DialogFooter, DialogType } from '@fluentui/react/lib/Dialog';
import { Spinner, SpinnerSize } from '@fluentui/react/lib/Spinner';
import { Stack } from '@fluentui/react/lib/Stack';
import * as React from 'react';
import { createRoot } from 'react-dom/client';
import { LuisText } from 'src/core/components/LuisText';
import { ChangeSavingStateEvent } from 'src/core/types/ConfirmLeaveEvent';
import { getEventBusV2 } from 'src/core/utils/eventBusV2';
import { partial } from 'src/core/utils/function';

const jsConfirmLeaveModalRootClassName = 'confirm-leave-modal-root';

// This is the div that will host the leave modal component
export const getConfirmLeaveModalHost = () => {
    let confirmLeaveModalRoot = document.body.querySelector(`.${jsConfirmLeaveModalRootClassName}`);
    if (!confirmLeaveModalRoot) {
        confirmLeaveModalRoot = document.createElement('div');
        confirmLeaveModalRoot.classList.add(jsConfirmLeaveModalRootClassName);
    }
    document.body.appendChild(confirmLeaveModalRoot);

    return confirmLeaveModalRoot;
};

export type ConfirmLeaveModalProps = {
    /**
     * Title of the leave dialog.
     */
    title: string;
    /**
     * Description of the leave dialog.
     */
    description: string;
    /**
     * Leave confirm button text.
     */
    leaveButtonText: string;
    /**
     * Leave cancel button text.
     */
    cancelButtonText: string;
    /**
     * Leave confirm callback.
     */
    onConfirm: (confirm: boolean) => void;
    /**
     * Dismiss modal callback.
     */
    onDismiss: () => void;
    /**
     * Optional is for the modal.
     */
    id?: string;
    /**
     * A boolean flag indicating if the leave modal behavior is to leave with an action
     * fired on confirmation.
     */
    leaveAnyway?: boolean;

    /**
     * A boolean flag indicating if the modal depends on
     * saving state to listen and apply onConfirm method.
     */
    dependsOnSavingState?: boolean;
};

/**
 * Modal to confirm leaving.
 */
export const ConfirmLeaveModal = (props: ConfirmLeaveModalProps) => {
    const { onConfirm, onDismiss, title, description, leaveButtonText, cancelButtonText } = props;
    const eventBusV2 = getEventBusV2();

    const [isDisabled, setIsDisabled] = React.useState(false);

    React.useEffect(() => {
        eventBusV2.on<ChangeSavingStateEvent>({
            kind: 'changeSavingState',
            callback: params => setIsDisabled(params.savingState === 'inProgress')
        });
    }, []);

    return (
        <Dialog
            minWidth={600}
            hidden={false}
            dialogContentProps={{
                type: DialogType.normal,
                title,
                closeButtonAriaLabel: 'Close',
                subText: description
            }}
            onDismiss={onDismiss}
            modalProps={{ isBlocking: true }}>
            <DialogFooter>
                <PrimaryButton disabled={isDisabled} onClick={partial(onConfirm, true)}>
                    <Stack horizontal tokens={{ childrenGap: 5 }}>
                        {isDisabled && <Spinner size={SpinnerSize.xSmall} />}
                        <LuisText>{leaveButtonText}</LuisText>
                    </Stack>
                </PrimaryButton>
                <DefaultButton disabled={isDisabled} onClick={partial(onConfirm, false)} text={cancelButtonText} />
            </DialogFooter>
        </Dialog>
    );
};

// Ask user to confirm the leave
export const confirmLeave = async (modalProps: Omit<ConfirmLeaveModalProps, 'onConfirm' | 'onDismiss'>) => {
    const confirmLeaveModalHost = getConfirmLeaveModalHost();
    const root = createRoot(confirmLeaveModalHost);
    return new Promise<boolean>(resolve => {
        const onConfirm = (confirm: boolean) => {
            root.unmount();
            resolve(confirm);
        };

        root.render(<ConfirmLeaveModal {...modalProps} onConfirm={onConfirm} onDismiss={() => onConfirm(false)} />);
    });
};
